import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaSpinner,
  FaTimesCircle,
  FaTools,
  FaWhatsapp,
} from 'react-icons/fa';
import bridgeAmendmentService from '../../services/bridgeAmendmentApi';

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

/**
 * CV-AMD — Customer Bridge amendment approval (no login).
 * Route: /bridge/amendment/:token
 */
const BridgeAmendment = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [expandedOriginal, setExpandedOriginal] = useState(false);
  const [showDeclineFlow, setShowDeclineFlow] = useState(false);
  const [waiverChecked, setWaiverChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await bridgeAmendmentService.getAmendment(token);
      setData(res);
      if (res.amendment?.alreadyDecided) {
        setResult({ status: res.amendment.decision });
      }
    } catch (err) {
      if (err.status === 401 || /expired/i.test(err.message || '')) {
        setResult({ status: 'expired' });
      } else {
        setError(err.message || 'Unable to load this update');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const amendment = data?.amendment;
  const waiverText = data?.waiverText || '';

  const whatsAppUrl = useMemo(() => {
    const phone = (data?.garagePhone || '').replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Hi, I have a question about the additional repair suggested for ${data?.vehicle?.registrationNumber || 'my vehicle'}.`
    );
    return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  }, [data]);

  const handleApprove = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await bridgeAmendmentService.approveAmendment(token, {
        jobCardId: data?.jobCardId,
      });
      setResult({ status: 'approved', orderId: res.orderId });
    } catch (err) {
      if (err.code === 'ALREADY_DECIDED') {
        setResult({ status: err.message?.includes('declined') ? 'declined' : 'approved' });
      } else {
        setError(err.message || 'Could not approve');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!waiverChecked) {
      setError('Please confirm the waiver to decline.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await bridgeAmendmentService.declineAmendment(token, { waiverAccepted: true });
      setResult({ status: 'declined' });
    } catch (err) {
      setError(err.message || 'Could not record your decision');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <FaSpinner className="animate-spin text-slate-500" />
      </div>
    );
  }

  if (result?.status === 'expired') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl bg-white border p-8 text-center">
          <p className="font-semibold text-slate-900">Link expired</p>
          <p className="text-sm text-slate-600 mt-2">Contact your garage for a new link.</p>
        </div>
      </div>
    );
  }

  if (result?.status === 'approved') {
    return (
      <div className="min-h-screen bg-emerald-50 flex flex-col items-center justify-center p-6 text-center">
        <FaCheckCircle className="text-5xl text-emerald-500 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Additional work approved</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-sm">
          Your garage will proceed with the extra repair. Parts order created
          {result.orderId ? ` (${result.orderId})` : ''}.
        </p>
        <a
          href="https://wa.me/"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-600 text-white px-6 py-3 text-sm font-semibold"
        >
          <FaWhatsapp /> Return to WhatsApp
        </a>
      </div>
    );
  }

  if (result?.status === 'declined') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <FaTimesCircle className="text-5xl text-slate-400 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Original scope confirmed</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-sm">
          Your garage will continue with the original repair only. Your waiver has been recorded.
        </p>
        <a
          href="https://wa.me/"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-600 text-white px-6 py-3 text-sm font-semibold"
        >
          <FaWhatsapp /> Return to WhatsApp
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-lg mx-auto">
          <p className="text-xs font-semibold text-amber-600 uppercase">SparePe</p>
          <h1 className="text-base font-bold text-slate-900 mt-0.5">Update from your mechanic</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">{error}</div>
        )}

        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
          <button
            type="button"
            onClick={() => setExpandedOriginal((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left bg-slate-50 hover:bg-slate-100"
          >
            <span className="text-sm font-semibold text-slate-800">Original repair summary</span>
            {expandedOriginal ? <FaChevronUp className="text-slate-500" /> : <FaChevronDown className="text-slate-500" />}
          </button>
          {expandedOriginal && (
            <div className="px-4 py-3 text-sm text-slate-600 whitespace-pre-wrap border-t border-slate-100">
              {amendment?.originalRepairSummary || '—'}
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm space-y-4">
          <div className="flex items-start gap-3">
            <FaTools className="text-xl text-amber-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase mb-1">New issue found</p>
              <p className="text-sm text-slate-800">{amendment?.plainIssue}</p>
            </div>
          </div>

          {(amendment?.additionalParts || []).length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase mb-2">Additional parts</p>
              <ul className="space-y-2">
                {amendment.additionalParts.map((p) => (
                  <li
                    key={p.plainName}
                    className="flex justify-between gap-2 text-sm border border-slate-100 rounded-lg px-3 py-2"
                  >
                    <span>
                      {p.plainName}
                      {p.quantity > 1 ? ` × ${p.quantity}` : ''}
                      {p.conditionLabel ? (
                        <span className="block text-xs text-slate-500">{p.conditionLabel}</span>
                      ) : null}
                    </span>
                    <span className="font-semibold shrink-0">{formatINR(p.price * (p.quantity || 1))}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {amendment?.additionalLabor?.amount > 0 && (
            <div className="flex justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
              <span>{amendment.additionalLabor.description || 'Additional labour'}</span>
              <span className="font-semibold">{formatINR(amendment.additionalLabor.amount)}</span>
            </div>
          )}

          <div className="flex justify-between pt-2 border-t border-slate-100 font-bold text-slate-900">
            <span>Additional cost</span>
            <span>{formatINR(amendment?.additionalTotal)}</span>
          </div>
        </div>

        {!showDeclineFlow ? (
          <div className="space-y-3">
            <button
              type="button"
              disabled={submitting}
              onClick={handleApprove}
              className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-4 font-bold text-base"
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2 justify-center">
                  <FaSpinner className="animate-spin" /> Processing…
                </span>
              ) : (
                'Approve Additional Work'
              )}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => setShowDeclineFlow(true)}
              className="w-full rounded-xl border-2 border-red-500 text-red-600 py-4 font-bold text-base bg-white"
            >
              Decline — proceed with original
            </button>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border-2 border-red-200 p-5 space-y-4">
            <p className="text-sm font-semibold text-red-900">Confirm decline</p>
            <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={waiverChecked}
                onChange={(e) => setWaiverChecked(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300"
              />
              <span>{waiverText}</span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeclineFlow(false);
                  setWaiverChecked(false);
                }}
                className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting || !waiverChecked}
                onClick={handleDecline}
                className="flex-1 rounded-xl bg-red-600 disabled:bg-slate-300 text-white py-3 text-sm font-bold"
              >
                Confirm decline
              </button>
            </div>
          </div>
        )}

        <a
          href={whatsAppUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-sm text-green-700 font-semibold"
        >
          Questions? Message your garage on WhatsApp
        </a>
      </main>
    </div>
  );
};

export default BridgeAmendment;
